import {
  Box,
  Button,
  Flex,
  keyframes,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { getBankAccountDisplay } from '../utils';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import { useGetBankAccountsByCreditFacilityIdQuery } from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useGetCreditFacilityCapitalProviderRepresentativeQuery } from '~/packages/credit-facility/requests/requests.gql.generated';

// Keyframes for highlight animation for source bank account
const highlightBlinkAnimation = keyframes`
  0% { background-color: ${colors.red[50]}; }
  100% { background-color: transparent; }
`;

const BlinkableBox = ({
  isBlinking,
  setIsBlinking,
  children,
}: {
  isBlinking: boolean;
  setIsBlinking: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactElement;
}) => {
  useEffect(() => {
    // Reset the blinking state after the animation duration
    if (isBlinking) {
      const timer = setTimeout(() => setIsBlinking(false), 1000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isBlinking, setIsBlinking]);

  return (
    <>
      <Box
        backgroundColor={isBlinking ? colors.red[50] : 'transparent'}
        animation={
          isBlinking
            ? `${highlightBlinkAnimation} 0.6s ease-in-out forwards`
            : undefined
        }
      >
        {children}
      </Box>
    </>
  );
};

export const CreateInvoicePayment = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();
  const {
    invoice,
    updateInvoice,
    selectedEntities: {
      bankAccounts: {
        updateSourceBankAccount,
        updateDestinationBankAccount,
        // maximumAvailableAmountCash,
        // updateMaximumAvailableAmountCash,
      },
    },
  } = useCreateInvoiceContext();
  const { facilities } = useCreditFacilitiesOrThrow();

  const [
    isSourceBankAccountBoxBlinking,
    setIsSourceBankAccountBoxBlinking,
  ] = useState<boolean>(false);

  // const {
  //   data: latestBorrowingBaseReportData,
  //   loading: isLatestBorrowingBaseReportLoading,
  // } = useGetLatestBorrowingBaseReportQuery({
  //   variables: { creditFacilityId: invoice.creditFacilityId },
  // });

  const {
    data: bankAccountsData,
    loading: isBankAccountsLoading,
  } = useGetBankAccountsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    variables: {
      organizationId: user.organizationId,
      creditFacilityId: invoice.creditFacilityId,
    },
  });

  const capitalProviderRepresentativeOrganizationQuery = useGetCreditFacilityCapitalProviderRepresentativeQuery(
    {
      variables: {
        creditFacilityId: invoice.creditFacilityId,
      },
    },
  );
  if (capitalProviderRepresentativeOrganizationQuery.error) {
    throw capitalProviderRepresentativeOrganizationQuery.error;
  }

  const bankAccountOptions = useMemo(() => {
    if (!bankAccountsData?.bankAccountsWithDesignationAndOwner) {
      return null;
    }

    const sourceBankAccountOptions: { value: string; label: string }[] = [];
    const destinationBankAccountOptions: {
      value: string;
      label: string;
    }[] = [];

    bankAccountsData.bankAccountsWithDesignationAndOwner.forEach(
      (bankAccount) => {
        const selectedCreditFacility = facilities.find(
          (facility) => facility.id === invoice.creditFacilityId,
        );

        finleyAssert(
          selectedCreditFacility,
          'Credit Facility must be selected to choose payment sources.',
        );

        const bankAccountOption = {
          value: bankAccount.id,
          label: getBankAccountDisplay({
            institutionName: bankAccount.institutionName,
            accountName: bankAccount.name,
            accountNumber: bankAccount.accountNumber,
          }),
        };

        // source bank accounts of an invoice must be owned by the borrower
        // destination bank accounts are currently restricted to the capital provider representative organization owned accounts
        if (bankAccount.organizationId === selectedCreditFacility.borrowerId) {
          sourceBankAccountOptions.push(bankAccountOption);
        } else if (
          bankAccount.organizationId ===
          capitalProviderRepresentativeOrganizationQuery.data?.creditFacility
            .capitalProviderRepresentativeOrganization?.id
        ) {
          destinationBankAccountOptions.push(bankAccountOption);
        }
      },
    );

    return {
      sourceBankAccountOptions,
      destinationBankAccountOptions,
    };
  }, [
    bankAccountsData,
    facilities,
    invoice.creditFacilityId,
    capitalProviderRepresentativeOrganizationQuery.data?.creditFacility
      .capitalProviderRepresentativeOrganization?.id,
  ]);

  useEffect(() => {
    if (bankAccountOptions && invoice.sourceBankAccountId) {
      const sourceBankAccount = bankAccountsData?.bankAccountsWithDesignationAndOwner.find(
        (bankAccount) => bankAccount.id === invoice.sourceBankAccountId,
      );

      updateSourceBankAccount(sourceBankAccount ?? null);
    }

    if (bankAccountOptions && invoice.destinationBankAccountId) {
      const destinationBankAccount = bankAccountsData?.bankAccountsWithDesignationAndOwner.find(
        (bankAccount) => bankAccount.id === invoice.destinationBankAccountId,
      );

      updateDestinationBankAccount(destinationBankAccount ?? null);
    }
  }, [
    bankAccountOptions,
    bankAccountsData,
    invoice.sourceBankAccountId,
    invoice.destinationBankAccountId,
    updateSourceBankAccount,
    updateDestinationBankAccount,
  ]);

  // @dani todo - add the below back in once we support implementations with borrowing base for invoices
  // const totalBorrowingBaseAmountDerived = useMemo(() => {
  //   if (!latestBorrowingBaseReportData?.latestBorrowingBaseReport) {
  //     return null;
  //   }

  //   return (
  //     (latestBorrowingBaseReportData.latestBorrowingBaseReport
  //       .maxReceivableAdvanceAmount ?? 0) +
  //     (latestBorrowingBaseReportData.latestBorrowingBaseReport
  //       .totalCashBorrowingBaseAmount ?? 0)
  //   );
  // }, [latestBorrowingBaseReportData?.latestBorrowingBaseReport]);

  // const canClickContinue = useMemo(() => {
  //   const isBorrowingBaseAvailabilityPassed =
  //     _.isNumber(maximumAvailableAmountCash) &&
  //     Boolean(maximumAvailableAmountCash >= 0);

  //   if (
  //     sourceBankAccount?.accountType === 'collections' &&
  //     !isBorrowingBaseAvailabilityPassed
  //   ) {
  //     return false;
  //   }
  //   return true;
  // }, [sourceBankAccount, maximumAvailableAmountCash]);

  if (isBankAccountsLoading || !bankAccountsData || !bankAccountOptions) {
    return (
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={1} />
        <Skeleton height="200px" mt="40px" />
      </Box>
    );
  }

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={1} />
      </Box>

      {/* Guided Form Step 2 */}
      <Flex direction="column" flexGrow={1}>
        <Box mb="40px">
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontWeight={600} mb="16px">
              Source Bank Account
            </Text>
            <Text color={colors.gray[500]}>Optional</Text>
          </Flex>
          <BlinkableBox
            isBlinking={isSourceBankAccountBoxBlinking}
            setIsBlinking={setIsSourceBankAccountBoxBlinking}
          >
            <SearchSelect
              options={bankAccountOptions.sourceBankAccountOptions}
              value={bankAccountOptions.sourceBankAccountOptions.find(
                (option) => option.value === invoice.sourceBankAccountId,
              )}
              onChange={(bankAccountId) => {
                updateInvoice({
                  field: 'sourceBankAccountId',
                  value: !bankAccountId ? null : bankAccountId,
                });

                if (!bankAccountId) {
                  updateSourceBankAccount(null);
                } else {
                  const newSourceBankAccount = bankAccountsData.bankAccountsWithDesignationAndOwner.find(
                    (bankAccount) => bankAccount.id === bankAccountId,
                  );

                  finleyAssert(
                    newSourceBankAccount,
                    `Bank Account must exist for selected bank account ID [bankAccountId: ${bankAccountId}]`,
                  );

                  updateSourceBankAccount(newSourceBankAccount);

                  // @dani todo - add this back in once we support implementations with borrowing base for invoices
                  // if (
                  //   latestBorrowingBaseReportData?.latestBorrowingBaseReport
                  // ) {
                  //   const borrowingBaseReport =
                  //     latestBorrowingBaseReportData.latestBorrowingBaseReport;

                  //   const borrowingBaseAvailabilityCash = commonUtils.divideByOrZero(
                  //     Math.min(
                  //       (totalBorrowingBaseAmountDerived ?? 0) -
                  //         borrowingBaseReport.currentPrincipalBalance,
                  //       borrowingBaseReport.totalCashBorrowingBaseAmount ?? 0,
                  //     ),
                  //     borrowingBaseReport.cashAdvanceRate,
                  //   );

                  //   updateMaximumAvailableAmountCash(
                  //     borrowingBaseAvailabilityCash,
                  //   );
                  // } else {
                  //   updateMaximumAvailableAmountCash(null);
                  // }
                }
              }}
              isSelectedColor={colors.gray[50]}
              isFocusedColor={colors.gray[50]}
              placeholder="Select"
              isClearable
            />
          </BlinkableBox>
        </Box>

        <Box mb="40px">
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontWeight={600} mb="16px">
              Destination Bank Account
            </Text>
            <Text color={colors.gray[500]}>Optional</Text>
          </Flex>
          <SearchSelect
            options={bankAccountOptions.destinationBankAccountOptions}
            value={bankAccountOptions.destinationBankAccountOptions.find(
              (option) => option.value === invoice.destinationBankAccountId,
            )}
            onChange={(bankAccountId) => {
              updateInvoice({
                field: 'destinationBankAccountId',
                value: !bankAccountId ? null : bankAccountId,
              });

              if (!bankAccountId) {
                updateDestinationBankAccount(null);
              } else {
                const newDestinationBankAccount = bankAccountsData.bankAccountsWithDesignationAndOwner.find(
                  (bankAccount) => bankAccount.id === bankAccountId,
                );

                finleyAssert(
                  newDestinationBankAccount,
                  `Bank Account must exist for selected bank account ID [bankAccountId: ${bankAccountId}]`,
                );

                updateDestinationBankAccount(newDestinationBankAccount);
              }
            }}
            isSelectedColor={colors.gray[50]}
            isFocusedColor={colors.gray[50]}
            placeholder="Select"
            isClearable
          />
        </Box>

        {/* @dani todo - add this back in once we support implementations with borrowing base for invoices */}
        {/* {sourceBankAccount?.accountType === 'collections' && (
          <Box mb="40px">
            {isLatestBorrowingBaseReportLoading && (
              <CreateInvoiceBorrowingBaseAvailabilityLoading />
            )}

            {!latestBorrowingBaseReportData?.latestBorrowingBaseReport && (
              <CreateInvoiceBorrowingBaseAvailabilityNoData />
            )}

            {latestBorrowingBaseReportData?.latestBorrowingBaseReport &&
              _.isNumber(totalBorrowingBaseAmountDerived) && (
                <CreateInvoiceBorrowingBaseAvailability
                  onClickSetSourceBankAccount={() =>
                    setIsSourceBankAccountBoxBlinking(true)
                  }
                  reportingDate={
                    latestBorrowingBaseReportData.latestBorrowingBaseReport
                      .reportingDate
                  }
                  totalBorrowingBaseAmountDerived={
                    totalBorrowingBaseAmountDerived
                  }
                  totalCashBorrowingBaseAmount={
                    latestBorrowingBaseReportData.latestBorrowingBaseReport
                      .totalCashBorrowingBaseAmount ?? 0
                  }
                  currentPrincipalBalance={
                    latestBorrowingBaseReportData.latestBorrowingBaseReport
                      .currentPrincipalBalance ?? 0
                  }
                />
              )}
          </Box>
        )} */}
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" py="40px">
        <Button variant="v2-ghost" onClick={() => setCurrentStepIndex(0)}>
          Back
        </Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          onClick={() => setCurrentStepIndex(2)}
          // isDisabled={!canClickContinue}
        >
          Continue
        </Button>
      </Flex>
    </Flex>
  );
};
